// Typed wrappers around /v1/support. Mirrors lib/api/agents.ts so the
// support page and the agents page share the same useQuery / useMutation
// idioms — list, get, create, addMessage, acknowledge.

import type {
  AddSupportMessageInput,
  CreateSupportTicketInput,
  SupportListResponse,
  SupportMessage,
  SupportStatus,
  SupportTicket,
  SupportTicketDetail,
  UpdateSupportTicketInput,
} from '@cortex/shared';
import type { ApiClient } from '../api-client.js';

export interface ListSupportOptions {
  status?: SupportStatus;
  limit?: number;
}

function listPath(opts: ListSupportOptions): string {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return qs ? `/v1/support?${qs}` : '/v1/support';
}

export const supportApi = {
  list: (api: ApiClient, opts: ListSupportOptions = {}) =>
    api.get<SupportListResponse>(listPath(opts)),
  get: (api: ApiClient, id: string) =>
    api.get<SupportTicketDetail>(`/v1/support/${id}`),
  create: (api: ApiClient, body: CreateSupportTicketInput) =>
    api.post<SupportTicketDetail>('/v1/support', body),
  addMessage: (api: ApiClient, id: string, body: AddSupportMessageInput) =>
    api.post<SupportMessage>(`/v1/support/${id}/messages`, body),
  // Customer-side transition. Today only `acknowledgeResolution: true` is
  // accepted; other patches return a 400. Returns the updated ticket
  // (status='closed', closedAt set).
  update: (api: ApiClient, id: string, body: UpdateSupportTicketInput) =>
    api.patch<SupportTicket>(`/v1/support/${id}`, body),
} as const;

export const SUPPORT_QUERY_KEY = ['support'] as const;
export const supportTicketQueryKey = (id: string) => ['support', id] as const;
